import { NotificationFlagsCategories } from '../models/NotificationFlags/NotificationFlagsCategories';

export interface TimelineFlagScope {
    engagementId: number;
    timelineId?: number;
}

type CodeSegment = string | number | undefined | null;

const SEPARATOR = '.';
const TIMELINE_SEGMENT_PATTERN = new RegExp(
    `\\${SEPARATOR}${NotificationFlagsCategories.TimelineCode}\\${SEPARATOR}(\\d+)(?=\\${SEPARATOR}|$)`,
    'i'
);

const join = (...segments: CodeSegment[]): string =>
    segments
        .filter((segment) => segment !== undefined && segment !== null)
        .join(SEPARATOR);

const withTimeline = (root: string, timelineId?: number): string =>
    timelineId === undefined || timelineId === null
        ? root
        : join(root, NotificationFlagsCategories.TimelineCode, timelineId);

const sellerRoot = (scope: TimelineFlagScope): string =>
    withTimeline(join(NotificationFlagsCategories.MpCode, scope.engagementId), scope.timelineId);

const buyerRoot = (scope: TimelineFlagScope): string =>
    withTimeline(
        join(
            NotificationFlagsCategories.MpCode,
            NotificationFlagsCategories.OpportunitiesCode,
            scope.engagementId
        ),
        scope.timelineId
    );

export const marketplaceFlagCodes = {
    sellerTimelinePreview: (scope: TimelineFlagScope, eventId?: string): string =>
        join(sellerRoot(scope), NotificationFlagsCategories.EngagementTimelinePreviewCode, eventId),

    buyerTimelinePreview: (scope: TimelineFlagScope, eventId?: string): string =>
        join(buyerRoot(scope), NotificationFlagsCategories.EngagementTimelinePreviewCode, eventId),

    timelinePreview: (scope: TimelineFlagScope, isBuyerView: boolean, eventId?: string): string =>
        isBuyerView
            ? marketplaceFlagCodes.buyerTimelinePreview(scope, eventId)
            : marketplaceFlagCodes.sellerTimelinePreview(scope, eventId),

    sellerDeadlineDocket: (scope: TimelineFlagScope, eventId?: string): string =>
        join(sellerRoot(scope), NotificationFlagsCategories.DeadlineDocketCode, eventId),

    buyerDeadlineDocket: (scope: TimelineFlagScope, eventId?: string): string =>
        join(buyerRoot(scope), NotificationFlagsCategories.DeadlineDocketCode, eventId),

    buyerMasterDeadlineDocket: (engagementId?: number, eventId?: string): string =>
        join(
            NotificationFlagsCategories.MpCode,
            NotificationFlagsCategories.MasterDeadlineDocketCode,
            engagementId,
            eventId
        ),

    buyerState: (scope: TimelineFlagScope, buyerId: number, eventId: string): string =>
        join(
            sellerRoot(scope),
            NotificationFlagsCategories.BuyersCode,
            buyerId,
            NotificationFlagsCategories.BuyerStateCode,
            eventId
        ),

    buyerStatus: (scope: TimelineFlagScope, buyerId: number, stageId: number): string =>
        join(
            sellerRoot(scope),
            NotificationFlagsCategories.BuyersCode,
            buyerId,
            NotificationFlagsCategories.BuyerStatusCode,
            stageId
        ),

    buyerOpportunityStatus: (scope: TimelineFlagScope): string =>
        join(buyerRoot(scope), NotificationFlagsCategories.OpportunityStatusCode),

    // The seller's badges about one buyer, whichever timeline wrote them.
    sellerBuyerRow: (scope: TimelineFlagScope, buyerId: number): string =>
        join(sellerRoot(scope), NotificationFlagsCategories.BuyersCode, buyerId) + SEPARATOR,

    // Everything the seller side holds under one timeline of the engagement.
    sellerTimeline: (scope: TimelineFlagScope): string => sellerRoot(scope),

    // Prefixes for every timeline of one engagement. The closing separator keeps engagement 4 from
    // also matching engagement 42.
    sellerEngagement: (engagementId: number): string =>
        join(NotificationFlagsCategories.MpCode, engagementId) + SEPARATOR,

    buyerOpportunity: (engagementId: number): string =>
        join(
            NotificationFlagsCategories.MpCode,
            NotificationFlagsCategories.OpportunitiesCode,
            engagementId
        ) + SEPARATOR,
};

export const timelineIdOf = (code: string): number | undefined => {
    const match = TIMELINE_SEGMENT_PATTERN.exec(code);
    return match ? Number(match[1]) : undefined;
};

export const stripTimelineSegment = (code: string): string =>
    code.replace(TIMELINE_SEGMENT_PATTERN, '');

export const flagCodeMatches = (
    flagCode: string,
    expectedCode: string,
    asPrefix: boolean = false
): boolean => {
    const expectedTimelineId = timelineIdOf(expectedCode);
    const flagTimelineId = timelineIdOf(flagCode);
    const bothNameATimeline = expectedTimelineId !== undefined && flagTimelineId !== undefined;
    if (bothNameATimeline && expectedTimelineId !== flagTimelineId) {
        return false;
    }

    const flag = stripTimelineSegment(flagCode).toLowerCase();
    const expected = stripTimelineSegment(expectedCode).toLowerCase();
    return asPrefix ? flag.startsWith(expected) : flag === expected;
};

// A step is flagged by the main's event id: a buyer timeline's copy of a step has a row id of its
// own, but with the serving switch off the buyer's screens show the main's rows, and with it on
// the copy carries the main's id beside its own. Matching on this id works in both modes.
export const flagEventId = (event: { id: string; masterEventId?: string }): string =>
    event.masterEventId ?? event.id;

// Badges that name that buyer from any timeline, plus everything under the buyer's own timeline.
export const buyerRowBadgeMatcher = (
    engagementId: number,
    buyerId: number,
    ownTimelineId?: number
): ((flagCode: string) => boolean) => {
    const namesTheBuyer = (code: string): boolean =>
        stripTimelineSegment(code)
            .toLowerCase()
            .startsWith(
                marketplaceFlagCodes.sellerBuyerRow({ engagementId }, buyerId).toLowerCase()
            );

    const namesAnyBuyer = (code: string): boolean =>
        stripTimelineSegment(code)
            .toLowerCase()
            .startsWith(join(NotificationFlagsCategories.MpCode, engagementId, NotificationFlagsCategories.BuyersCode).toLowerCase() + SEPARATOR);

    const underOwnTimeline = (code: string): boolean =>
        ownTimelineId !== undefined &&
        timelineIdOf(code) === ownTimelineId &&
        code.toLowerCase().startsWith(join(NotificationFlagsCategories.MpCode, engagementId).toLowerCase() + SEPARATOR) &&
        !namesAnyBuyer(code);

    return (flagCode: string): boolean => namesTheBuyer(flagCode) || underOwnTimeline(flagCode);
};
